// BUILD-042 simple benefits (migration 207) — HR Administration > Benefit plans.
// HR-managed benefit INFORMATION only: name, short description, provider/contact, external links
// and an optional reference to an existing Form Library document. Eligibility is managed per
// employee on the employee's own HR page (AdminBenefitsEligibilitySection), not here.
//
// Every action re-authorizes server-side (hr.benefit.manage, company scope). This panel only
// reflects what the server already permitted. Consistent with the accepted compact HR layout:
// a list first, a focused create/edit form, clear buttons, collapsible help, honest states.
import { useEffect, useState } from 'react';
import {
  listHrBenefitPlans, createHrBenefitPlan, updateHrBenefitPlan, archiveHrBenefitPlan, restoreHrBenefitPlan,
  listHrDocumentTemplates,
  type HrBenefitPlan, type HrBenefitPlanInput, type HrDocumentTemplate,
} from '../../api/client';
import { CollapsibleHelp } from './CollapsibleHelp';

interface LinkDraft { label: string; url: string }
interface PlanDraft {
  name: string; category: string; shortDescription: string;
  providerName: string; providerContact: string; externalUrl: string;
  documentTemplateId: string; published: boolean; links: LinkDraft[];
  /** Cycle 12: optional default, N CALENDAR days after the hire date. '' = no rule. Never pre-filled with a company policy value. */
  waitingPeriod: string;
}

const EMPTY_DRAFT: PlanDraft = {
  name: '', category: '', shortDescription: '', providerName: '', providerContact: '',
  externalUrl: '', documentTemplateId: '', published: true, links: [], waitingPeriod: '',
};

export const WAITING_PERIOD_LABEL = 'Waiting period (calendar days after hire date)';
export const WAITING_PERIOD_HELP = 'A default that suggests the eligibility date. Two calendar months is not the same as 60 days. HR still chooses each person’s date.';
export const WAITING_PERIOD_INVALID = 'Enter a whole number of days from 0 to 3650, or leave it empty for no waiting-period rule.';
/** '' => null (no rule); a whole number 0..3650 => that number; anything else => undefined (invalid). */
export function parseWaitingPeriod(raw: string): number | null | undefined {
  const t = raw.trim();
  if (t === '') return null;
  if (!/^\d{1,4}$/.test(t)) return undefined;
  const n = Number(t);
  return n >= 0 && n <= 3650 ? n : undefined;
}

function toDraft(p: HrBenefitPlan): PlanDraft {
  return {
    name: p.name, category: p.category ?? '', shortDescription: p.shortDescription ?? '',
    providerName: p.providerName ?? '', providerContact: p.providerContact ?? '',
    externalUrl: p.externalUrl ?? '', documentTemplateId: p.documentTemplateId ?? '',
    published: p.published, links: p.links.map((l) => ({ label: l.label, url: l.url })),
    waitingPeriod: typeof p.waitingPeriodDays === 'number' ? String(p.waitingPeriodDays) : '',
  };
}

function draftToInput(d: PlanDraft): HrBenefitPlanInput {
  const trimLinks = d.links.map((l) => ({ label: l.label.trim(), url: l.url.trim() })).filter((l) => l.label || l.url);
  return {
    name: d.name.trim(),
    category: d.category.trim() || null,
    shortDescription: d.shortDescription.trim() || null,
    providerName: d.providerName.trim() || null,
    providerContact: d.providerContact.trim() || null,
    externalUrl: d.externalUrl.trim() || null,
    documentTemplateId: d.documentTemplateId || null,
    published: d.published,
    waitingPeriodDays: parseWaitingPeriod(d.waitingPeriod) ?? null,
    links: trimLinks,
  };
}

/** `canListDocuments` is false for a benefit manager who does not also hold document authority: the Form Library list would be refused, so it is not requested. */
export function BenefitPlansPanel({ canListDocuments = true }: { canListDocuments?: boolean } = {}) {
  const [plans, setPlans] = useState<HrBenefitPlan[] | null>(null);
  const [documents, setDocuments] = useState<HrDocumentTemplate[]>([]);
  const [showArchived, setShowArchived] = useState(false);
  const [open, setOpen] = useState<string | 'new' | null>(null);
  const [draft, setDraft] = useState<PlanDraft>(EMPTY_DRAFT);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [listError, setListError] = useState<string | null>(null);

  const refresh = () =>
    listHrBenefitPlans(showArchived)
      .then((d) => { setPlans(d.plans); setListError(null); })
      .catch(() => { setPlans((prev) => prev ?? []); setListError('Benefit plans could not be loaded right now.'); });
  useEffect(() => { refresh(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [showArchived]);
  useEffect(() => {
    // The optional document reference reuses the existing Form Library — never a new store. A
    // failed load simply leaves the dropdown empty; it never blocks benefit management.
    if (!canListDocuments) return;
    listHrDocumentTemplates().then((d) => setDocuments(d.templates)).catch(() => setDocuments([]));
  }, [canListDocuments]);

  function startNew() { setDraft(EMPTY_DRAFT); setOpen('new'); setStatus(null); setNotice(null); }
  function startEdit(p: HrBenefitPlan) { setDraft(toDraft(p)); setOpen(p.planId); setStatus(null); setNotice(null); }
  function close() { setOpen(null); setStatus(null); }

  async function save() {
    if (saving) return;
    if (!draft.name.trim()) { setStatus('Enter a benefit name.'); return; }
    if (parseWaitingPeriod(draft.waitingPeriod) === undefined) { setStatus(`Not saved. ${WAITING_PERIOD_INVALID} Your changes are still here.`); return; }
    setSaving(true); setStatus(null);
    try {
      const input = draftToInput(draft);
      if (open === 'new') { await createHrBenefitPlan(input); }
      else if (open) { await updateHrBenefitPlan(open, input); }
      await refresh();
      setNotice(`Saved “${input.name}”.`);
      close();
    } catch (e: any) {
      // Every typed edit stays on screen — a failed save never discards the form.
      setStatus(`Not saved. ${e?.message || 'The benefit could not be saved.'} Your changes are still here.`);
    } finally { setSaving(false); }
  }

  async function archive(p: HrBenefitPlan) {
    setListError(null); setNotice(null);
    try { await archiveHrBenefitPlan(p.planId); await refresh(); setNotice(`“${p.name}” was retired. It is hidden from employees and from new eligibility, but every recorded decision is kept.`); }
    catch { setListError(`“${p.name}” couldn’t be retired right now. Nothing was changed.`); }
  }
  async function restore(p: HrBenefitPlan) {
    setListError(null); setNotice(null);
    try { await restoreHrBenefitPlan(p.planId); await refresh(); setNotice(`“${p.name}” was restored.`); }
    catch { setListError(`“${p.name}” couldn’t be restored right now. Nothing was changed.`); }
  }

  // ── Focused create / edit form ────────────────────────────────────────────────────────────
  if (open !== null) {
    const isNew = open === 'new';
    return (
      <div>
        <h2 className="text-lg font-semibold text-gray-900 leading-tight mb-1">{isNew ? 'Add a benefit' : 'Edit benefit'}</h2>
        <p className="text-sm text-gray-500 mb-4">Information employees see. This does not enroll anyone or change payroll.</p>
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-5 space-y-4 max-w-2xl">
          <Field label="Name" required>
            <input className={inputCls} value={draft.name} autoFocus placeholder="e.g. Health insurance"
              onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))} />
          </Field>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Category (optional)">
              <input className={inputCls} value={draft.category} placeholder="e.g. Health, Retirement"
                onChange={(e) => setDraft((d) => ({ ...d, category: e.target.value }))} />
            </Field>
            <Field label="Provider (optional)">
              <input className={inputCls} value={draft.providerName} placeholder="e.g. Blue Cross Blue Shield"
                onChange={(e) => setDraft((d) => ({ ...d, providerName: e.target.value }))} />
            </Field>
          </div>
          <Field label="Short description (optional)">
            <textarea className={`${inputCls} min-h-[72px]`} value={draft.shortDescription} placeholder="A plain-language summary employees will read."
              onChange={(e) => setDraft((d) => ({ ...d, shortDescription: e.target.value }))} />
          </Field>
          <Field label="Provider contact (optional)">
            <input className={inputCls} value={draft.providerContact} placeholder="Phone, email, or group number"
              onChange={(e) => setDraft((d) => ({ ...d, providerContact: e.target.value }))} />
          </Field>
          <Field label="Main link (optional)">
            <input className={inputCls} value={draft.externalUrl} placeholder="https://…"
              onChange={(e) => setDraft((d) => ({ ...d, externalUrl: e.target.value }))} />
          </Field>

          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-medium text-gray-600">Extra links (optional)</span>
              <button type="button" className={linkBtnCls} onClick={() => setDraft((d) => ({ ...d, links: [...d.links, { label: '', url: '' }] }))}>Add link</button>
            </div>
            {draft.links.length === 0 && <p className="text-xs text-gray-400">No extra links.</p>}
            <div className="space-y-2">
              {draft.links.map((l, i) => (
                <div key={i} className="flex gap-2 items-start">
                  <input className={`${inputCls} w-1/3`} placeholder="Label" value={l.label}
                    onChange={(e) => setDraft((d) => ({ ...d, links: d.links.map((x, j) => j === i ? { ...x, label: e.target.value } : x) }))} />
                  <input className={`${inputCls} flex-1`} placeholder="https://…" value={l.url}
                    onChange={(e) => setDraft((d) => ({ ...d, links: d.links.map((x, j) => j === i ? { ...x, url: e.target.value } : x) }))} />
                  <button type="button" className="text-xs text-red-600 hover:underline mt-2" onClick={() => setDraft((d) => ({ ...d, links: d.links.filter((_, j) => j !== i) }))}>Remove</button>
                </div>
              ))}
            </div>
          </div>

          <Field label="Reference document (optional)">
            <select className={inputCls} value={draft.documentTemplateId}
              onChange={(e) => setDraft((d) => ({ ...d, documentTemplateId: e.target.value }))}>
              <option value="">No document reference</option>
              {documents.map((t) => <option key={t.templateId} value={t.templateId}>{t.name}</option>)}
            </select>
            <p className="text-xs text-gray-400 mt-1">Reuses an existing Form Library document. No file is copied.</p>
          </Field>

          <div>
            <label htmlFor="benefit-waiting-period" className="text-xs font-medium text-gray-600 block mb-1">{WAITING_PERIOD_LABEL} <span className="font-normal text-gray-400">(optional)</span></label>
            <input id="benefit-waiting-period" type="number" inputMode="numeric" min={0} max={3650} step={1} className={`${inputCls} sm:w-48${parseWaitingPeriod(draft.waitingPeriod) === undefined ? ' border-red-400' : ''}`}
              value={draft.waitingPeriod} onChange={(e) => setDraft((d) => ({ ...d, waitingPeriod: e.target.value }))}
              aria-describedby={parseWaitingPeriod(draft.waitingPeriod) === undefined ? 'benefit-waiting-period-help benefit-waiting-period-msg' : 'benefit-waiting-period-help'}
              aria-invalid={parseWaitingPeriod(draft.waitingPeriod) === undefined ? true : undefined} data-testid="benefit-waiting-period" />
            <p id="benefit-waiting-period-help" className="text-xs text-gray-500 mt-1">{WAITING_PERIOD_HELP}</p>
            {parseWaitingPeriod(draft.waitingPeriod) === undefined && <p id="benefit-waiting-period-msg" role="alert" className="text-xs text-red-700 mt-1">{WAITING_PERIOD_INVALID}</p>}
          </div>

          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={draft.published} onChange={(e) => setDraft((d) => ({ ...d, published: e.target.checked }))} />
            Visible to employees (unpublished benefits are HR-only)
          </label>

          {status && <p className="text-sm text-red-600">{status}</p>}
          <div className="flex gap-2 pt-1">
            <button type="button" className={primaryBtnCls} disabled={saving} onClick={save}>{saving ? 'Saving…' : (isNew ? 'Add benefit' : 'Save changes')}</button>
            <button type="button" className={secondaryBtnCls} disabled={saving} onClick={close}>Cancel</button>
          </div>
        </div>
      </div>
    );
  }

  // ── List ────────────────────────────────────────────────────────────────────────────────────
  return (
    <div data-testid="benefit-plans-list">
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-3 mb-4">
        <div className="min-w-0">
          <h2 className="text-lg font-semibold text-gray-900 leading-tight">Benefit plans</h2>
          <p className="text-sm text-gray-500 mt-1">The benefit information employees see. Eligibility is set per employee on their HR page.</p>
        </div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 min-w-0">
          <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
            <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} data-testid="show-retired-benefits" />
            Show retired benefits
          </label>
          <button type="button" className={primaryBtnCls} onClick={startNew} data-testid="add-benefit">Add a benefit</button>
        </div>
      </div>

      {notice && <div role="status" className="mb-3 bg-[#EDF5FC] text-sm text-gray-700 rounded-md px-3 py-2">{notice}</div>}
      {/* A failed load is said plainly and offers Try again; it is never drawn as "No benefits yet". */}
      {listError && <div role="alert" className="mb-3 bg-red-50 text-sm text-red-700 rounded-md px-3 py-2" data-testid="benefit-list-error">{listError} <button type="button" className="underline" onClick={() => { void refresh(); }}>Try again</button></div>}

      {plans === null && <p className="text-sm text-gray-400 mt-2">Loading…</p>}
      {plans !== null && plans.length === 0 && !listError && (
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-5 text-sm text-gray-500">
          No benefits yet. Use “Add a benefit” to create the first one.
        </div>
      )}
      <div className="space-y-3">
        {(plans ?? []).map((p) => {
          const mainUrl = webAddress(p.externalUrl);
          return (
            <div key={p.planId} className={`bg-white rounded-lg border shadow-sm px-4 sm:px-5 py-4 min-w-0 break-words ${p.archivedAt ? 'border-gray-200 opacity-70' : 'border-gray-200'}`} data-testid={`benefit-card-${p.planId}`}>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    {/* The name is a link ONLY when the plan has one clear main web address; otherwise it is plain text. Extra links are listed below with their own labels. */}
                    {mainUrl
                      ? <a href={mainUrl} target="_blank" rel="noopener noreferrer" className="text-[15px] font-semibold text-[#1565c0] underline hover:text-[#0d47a1]" data-testid="benefit-name-link">{p.name}<ExternalIcon /><span className="sr-only"> (opens in a new tab)</span></a>
                      : <span className="text-[15px] font-semibold text-gray-900" data-testid="benefit-name-text">{p.name}</span>}
                    {p.category && <span className="text-xs text-gray-600 border border-gray-200 bg-gray-50 rounded px-1.5 py-0.5">{p.category}</span>}
                    {!p.published && <span className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">Not visible to employees</span>}
                    {p.archivedAt && <span className="text-xs text-gray-500 bg-gray-100 border border-gray-200 rounded px-1.5 py-0.5">Retired</span>}
                  </div>
                  {p.shortDescription && <p className="text-sm text-gray-600 mt-1 line-clamp-2">{p.shortDescription}</p>}
                  {p.providerName && <p className="text-xs text-gray-500 mt-1">{p.providerName}</p>}
                  {typeof p.waitingPeriodDays === 'number' && (
                    <p className="text-xs text-gray-700 mt-1" data-testid={`benefit-waiting-${p.planId}`}>Waiting period: {p.waitingPeriodDays} {p.waitingPeriodDays === 1 ? 'day' : 'days'} after the hire date <span className="text-gray-500">(suggests the eligibility date; HR still chooses)</span></p>
                  )}
                  {p.externalUrl && !mainUrl && <p className="text-xs text-amber-700 mt-1">The main link isn’t a web address (http or https), so the name is not a link. Fix it under Edit.</p>}
                  {p.links.length > 0 && (
                    <p className="text-xs text-gray-500 mt-2 flex flex-wrap items-center gap-x-3 gap-y-1" data-testid="benefit-extra-links">
                      <span>More links:</span>
                      {p.links.map((l) => {
                        const href = webAddress(l.url);
                        return href
                          ? <a key={l.linkId} href={href} target="_blank" rel="noopener noreferrer" className="text-[#1565c0] underline">{l.label}<ExternalIcon /><span className="sr-only"> (opens in a new tab)</span></a>
                          : <span key={l.linkId}>{l.label} (not a web address)</span>;
                      })}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-3 sm:flex-shrink-0">
                  {!p.archivedAt && <button type="button" className={secondaryBtnCls} onClick={() => startEdit(p)}>Edit</button>}
                  {p.archivedAt
                    ? <button type="button" className={secondaryBtnCls} onClick={() => restore(p)}>Restore</button>
                    : <button type="button" className="text-sm text-gray-600 hover:text-red-700 hover:underline" onClick={() => archive(p)}>Retire</button>}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-4">
        <CollapsibleHelp title="How this page works">
          <p>Add each benefit the company offers — for example Health, Dental and 401(k) — with a description, the provider, a link, and optionally a reference document from the Form Library.</p>
          <p className="mt-2">When a benefit has a main link, its name opens that link in a new tab. Any extra links stay listed under the benefit with their own labels.</p>
          <p className="mt-2">Marking an employee <strong>Eligible</strong> happens on that employee’s HR page. Eligibility is only a review outcome: it does <strong>not</strong> enroll anyone, start coverage, or change payroll.</p>
        </CollapsibleHelp>
      </div>
    </div>
  );
}

/** A clickable address must be http(s). Anything else (javascript:, data:, a bare word) is never made into a link; it stays visible and editable. */
function webAddress(raw: string | null | undefined): string | null {
  const value = (raw ?? '').trim();
  if (!value) return null;
  try { const u = new URL(value); return u.protocol === 'http:' || u.protocol === 'https:' ? value : null; } catch { return null; }
}

function ExternalIcon() {
  return (
    <svg viewBox="0 0 24 24" className="inline-block w-3.5 h-3.5 ml-1 align-[-2px]" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M14 4h6v6" /><path d="M20 4l-9 9" /><path d="M18 14v5a1 1 0 01-1 1H5a1 1 0 01-1-1V7a1 1 0 011-1h5" />
    </svg>
  );
}

const inputCls = 'border border-gray-300 rounded px-3 py-1.5 text-sm w-full bg-white';
const primaryBtnCls = 'text-sm font-medium bg-[#4AABDE] text-white rounded px-3 py-1.5 hover:bg-[#3b96c4] disabled:opacity-50';
const secondaryBtnCls = 'text-sm font-medium border border-gray-300 text-gray-700 rounded px-3 py-1.5 hover:bg-gray-50 disabled:opacity-50';
const linkBtnCls = 'text-xs text-[#4AABDE] hover:underline';

function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <div>
      <label className="text-xs font-medium text-gray-600 block mb-1">{label}{required && <span className="text-red-500"> *</span>}</label>
      {children}
    </div>
  );
}
